import { useAuth } from '../../providers/AuthProvider';
import { getRuntimeConfig } from '../../services/runtime-config';

/**
 * The three federated identity providers offered by the Application (Req 5.1).
 */
type FederatedProvider = 'Google' | 'Facebook' | 'Apple';

/**
 * A single, individually-branded control per provider. Each control serves BOTH
 * sign-up and sign-in — there is no separate federated register vs login path
 * (Req 5.2). The Apple control's visible label is exactly "Sign in with Apple"
 * (Req 5.1); Google/Facebook use "Continue with …".
 *
 * Branding is expressed through a distinct label + glyph per provider, using
 * only `var(--color-*)` tokens (the `npm run scan:colours` check forbids
 * hardcoded colours).
 *
 * Validates: Requirements 5.1, 5.2, 10.5.
 */
interface ProviderMeta {
  provider: FederatedProvider;
  label: string;
  /** A simple text glyph used as a distinguishing mark; purely decorative. */
  glyph: string;
}

const PROVIDERS: ProviderMeta[] = [
  { provider: 'Google', label: 'Continue with Google', glyph: 'G' },
  { provider: 'Facebook', label: 'Continue with Facebook', glyph: 'f' },
  // Req 5.1: this exact label is mandated for the Apple control.
  { provider: 'Apple', label: 'Sign in with Apple', glyph: '' },
];

export function FederatedButtons() {
  const { signInWithProvider, isLoading } = useAuth();

  // Which providers are enabled for this env is driven by runtime config
  // (config.json / Vite fallback). When the config object carries an
  // `enabledFederatedProviders` array we honour it exactly (including an empty
  // array → render nothing). When config isn't loaded or the field is absent,
  // default to showing all three (preserves prior behaviour).
  const enabled = getRuntimeConfig()?.enabledFederatedProviders;
  const visibleProviders = enabled
    ? PROVIDERS.filter((p) => enabled.includes(p.provider.toLowerCase()))
    : PROVIDERS;

  // Nothing to render (e.g. an explicit empty enabled set) → render nothing.
  if (visibleProviders.length === 0) {
    return null;
  }

  return (
    <div
      role="group"
      aria-label="Sign in with a provider"
      style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}
    >
      {visibleProviders.map(({ provider, label, glyph }) => (
        <button
          key={provider}
          type="button"
          onClick={() => signInWithProvider(provider)}
          disabled={isLoading}
          aria-label={label}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            width: '100%',
            padding: '0.75rem',
            backgroundColor: 'var(--color-surface)',
            color: 'var(--color-text)',
            border: '1px solid var(--color-border)',
            borderRadius: '4px',
            cursor: isLoading ? 'not-allowed' : 'pointer',
            fontSize: '1rem',
            opacity: isLoading ? 0.7 : 1,
          }}
        >
          <span
            aria-hidden="true"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '1.25rem',
              height: '1.25rem',
              fontWeight: 700,
              color: 'var(--color-brand)',
            }}
          >
            {glyph}
          </span>
          <span>{label}</span>
        </button>
      ))}
    </div>
  );
}
